import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useFamilyContext } from './FamilyContext';
import { collegeJourneyProgressService, collegeJourneyService } from '../lib/college-journey-service';
import { addCollegeWithEvents } from '../services/events/CollegeServiceIntegration';
import { TestingErrorRecovery } from '../components/Testing/TestingModeIndicator';
import TaskSynchronizationService from '../services/TaskSynchronizationService';
import StudentVerificationService from '../services/StudentVerificationService';
import EssaySlotService from '../services/EssaySlotService';
import { supabase } from '../lib/supabase';

const COLLEGE_CATEGORIES = {
  reach: { label: "Reach Schools", description: "Reach schools where acceptance is competitive" },
  target: { label: "Target Schools", description: "Schools where you're a competitive candidate" },
  safety: { label: "Safety Schools", description: "Schools where admission is likely" },
  other: { label: "Other Schools", description: "Schools we're researching or not actively pursuing" }
};

const ESSAY_TYPES = {
  personal: { 
    label: "Personal Statement", 
    description: "Your core story and narrative",
    typical_length: "650 words"
  },
  supplemental: {
    label: "Supplemental Essays",
    description: "School-specific questions and prompts",
    typical_length: "varies"
  },
  scholarship: {
    label: "Scholarship Essays",
    description: "Essays for merit-based aid",
    typical_length: "varies"
  },
  honors: {
    label: "Honors Program Essays", 
    description: "Essays for special programs",
    typical_length: "varies"
  }
};

const APPLICATION_ROUNDS = {
  early_action: { label: "Early Action", deadline_type: "November 1-15" },
  early_decision: { label: "Early Decision", deadline_type: "November 1-15" },
  regular_decision: { label: "Regular Decision", deadline_type: "January 1-15" },
  rolling: { label: "Rolling Admission", deadline_type: "varies" }
};

// Helper function to parse date strings in local timezone (not UTC)
// Fixes timezone issues where dates shift backward by one day
const parseLocalDate = (dateString) => {
  if (!dateString) return null;

  // If it's already a Date object, return it
  if (dateString instanceof Date) return dateString;

  // Parse YYYY-MM-DD format in local timezone
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

const TIMELINE_CATEGORIES = {
  testing: { label: "Testing", color: "azure" },
  applications: { label: "Applications", color: "emerald" },
  essays: { label: "Essays", color: "violet" },
  interviews: { label: "Interviews", color: "orange" },
  decisions: { label: "Decisions", color: "red" },
  visits: { label: "Campus Visits", color: "teal" },
  financial: { label: "Financial Aid", color: "amber" }
};


const CollegeContext = createContext();

export function useCollegeContext() {
  const context = useContext(CollegeContext);
  if (!context) {
    throw new Error('useCollegeContext must be used within a CollegeProvider');
  }
  return context;
}

const DEFAULT_ESSAYS = [
  {
    id: 'essay_duke_why',
    title: 'Why Duke Engineering',
    prompt: 'Duke seeks a talented, engaged student body that embodies the wide range of human experience; we believe that the diversity of our students makes our community stronger. If you\'d like to share a perspective you bring or experiences you\'ve had to help us understand you better, we encourage you to do so.',
    type: 'supplemental',
    college_id: 'duke',
    content: 'Growing up in a community where environmental issues were often dismissed as "not our problem," I learned early that engineering without awareness of social systems is incomplete. When our local river became polluted due to industrial runoff, I didn\'t just see a chemical problem—I saw a failure of communication between technical experts and policymakers.\n\nThis experience shaped my understanding that the most pressing challenges of our time sit at the intersection of technology and policy. Duke\'s unique position, with its strong engineering program alongside the Sanford School of Public Policy, represents exactly the kind of interdisciplinary approach I believe is necessary.\n\nThrough my work on renewable energy research at the local university lab, I\'ve seen how technical solutions mean nothing if they can\'t be effectively communicated to decision-makers. Duke\'s DukeEngage program would allow me to apply engineering principles in real policy contexts, particularly in environmental sustainability initiatives.\n\nAt Duke, I don\'t just want to design better solar panels—I want to understand how to implement them effectively in communities that need them most.',
    word_count: 187,
    word_limit: 250,
    status: 'in_progress',
    created_date: '2025-01-15T10:00:00Z',
    last_modified: '2025-01-20T14:30:00Z'
  },
  {
    id: 'essay_mit_community',
    title: 'MIT Community',
    prompt: 'Describe the world you come from; for example, your family, clubs, school, community, city, or town. How has that world shaped your reachs and aspirations?',
    type: 'supplemental',
    college_id: 'mit',
    content: 'My world is the intersection of a small rural town and cutting-edge technology. While my classmates often see these as contradictory, I\'ve learned they\'re complementary.\n\nIn my high school\'s makeshift robotics lab—really just a converted storage closet—our team of eight students built competition robots using donated parts and creative problem-solving. When we couldn\'t afford professional sensors, we learned to build our own. When our programming environment crashed repeatedly, we figured out workarounds.\n\nBut the real magic happened when I started tutoring middle school students in programming. Teaching 12-year-olds to code forced me to break down complex concepts and find new ways to explain algorithms. I realized that the collaborative, teaching-focused environment wasn\'t slowing me down—it was making me a better programmer.\n\nThis experience taught me that innovation isn\'t just about individual brilliance; it\'s about lifting others up and solving problems together. At MIT, I\'m excited to be part of a community where helping others succeed is seen as strength, not weakness.',
    word_count: 176,
    word_limit: 200,
    status: 'draft',
    created_date: '2025-01-10T09:00:00Z',
    last_modified: '2025-01-18T16:45:00Z'
  },
  {
    id: 'essay_uchicago_quirky',
    title: 'UChicago Uncommon Essay',
    prompt: 'What\'s so odd about odd numbers?',
    type: 'supplemental',
    college_id: 'uchicago',
    content: 'Odd numbers are democracy in mathematical form.\n\nThink about it: every democratic decision requires an odd number of decision-makers to avoid ties. Three-person panels, five-member committees, nine-justice Supreme Courts. We intuitively understand that odd numbers create resolution where even numbers create stalemate.\n\nBut there\'s something deeper here. Odd numbers resist symmetry—they refuse to be cleanly divided, to be perfectly balanced. They insist on a remainder, a leftover, something that doesn\'t quite fit. In a world that often demands neat categories and clean divisions, odd numbers embrace the messy reality of existence.\n\nI think this is why I\'m drawn to ethical philosophy, particularly around artificial intelligence. The questions that fascinate me most are the odd ones—the remainders that don\'t fit neatly into utilitarian or deontological frameworks. Should an autonomous vehicle prioritize the lives of its passengers over pedestrians? There\'s no even-numbered answer to this odd-numbered problem.\n\nJust as odd numbers force mathematical systems to account for remainders, ethical odd questions force us to remain humble about the limits of our moral frameworks.',
    word_count: 189,
    word_limit: 250,
    status: 'complete',
    created_date: '2025-01-05T11:00:00Z',
    last_modified: '2025-01-25T13:20:00Z'
  }
];

const DEFAULT_TIMELINE = [
  {
    id: 'timeline_duke_ed',
    title: 'Submit Duke Early Decision Application',
    description: 'Complete and submit ED application with all required materials',
    due_date: '2025-11-01',
    category: 'applications',
    college_id: 'duke',
    completed: false,
    created_date: '2025-01-15T10:00:00Z'
  },
  {
    id: 'timeline_mit_ea',
    title: 'Submit MIT Early Action Application',
    description: 'Complete EA application and submit by deadline',
    due_date: '2025-11-01',
    category: 'applications',
    college_id: 'mit',
    completed: false,
    created_date: '2025-01-15T10:00:00Z'
  },
  {
    id: 'timeline_uchicago_ea',
    title: 'Submit UChicago Early Action Application',
    description: 'Submit EA application with essays',
    due_date: '2025-11-01',
    category: 'applications',
    college_id: 'uchicago',
    completed: false,
    created_date: '2025-01-15T10:00:00Z'
  },
  {
    id: 'timeline_duke_visit',
    title: 'Duke Campus Visit',
    description: 'Attend Duke azure Devil Days - admitted student event',
    due_date: '2025-04-15',
    category: 'visits',
    college_id: 'duke',
    completed: true,
    completion_date: '2025-04-15T14:00:00Z',
    created_date: '2025-01-20T09:00:00Z'
  },
  {
    id: 'timeline_sat_retake',
    title: 'SAT Retake (October)',
    description: 'Final SAT attempt to improve math score',
    due_date: '2025-10-05',
    category: 'testing',
    college_id: null,
    completed: false,
    created_date: '2025-01-10T15:00:00Z'
  },
  {
    id: 'timeline_duke_interview',
    title: 'Duke Alumni Interview',
    description: 'Optional interview with local Duke alumni',
    due_date: '2025-12-01',
    category: 'interviews',
    college_id: 'duke',
    completed: false,
    created_date: '2025-01-22T11:00:00Z'
  },
  {
    id: 'timeline_fafsa',
    title: 'Submit FAFSA',
    description: 'Complete and submit FAFSA for financial aid',
    due_date: '2025-12-01',
    category: 'financial',
    college_id: null,
    completed: false,
    created_date: '2025-01-12T16:00:00Z'
  }
];

const DEFAULT_COMPARISONS = [
  {
    id: 'comparison_tech_vs_policy',
    title: 'Technical Focus vs. Policy Integration',
    description: 'Comparing how each school balances technical rigor with policy applications',
    colleges: ['duke', 'mit', 'uchicago'],
    criteria: [
      {
        name: 'Engineering Strength',
        scores: { duke: 8, mit: 10, uchicago: 6 }
      },
      {
        name: 'Policy Integration', 
        scores: { duke: 9, mit: 7, uchicago: 8 }
      },
      {
        name: 'Interdisciplinary Opportunities',
        scores: { duke: 9, mit: 8, uchicago: 9 }
      },
      {
        name: 'Research Opportunities',
        scores: { duke: 8, mit: 10, uchicago: 8 }
      }
    ],
    notes: 'Duke emerges as the best balance for interdisciplinary goals, while MIT offers unmatched technical depth.',
    created_date: '2025-01-25T10:00:00Z'
  }
];

export function CollegeProvider({ children }) {
  const { currentUser, family } = useFamilyContext();
  
  // Core data states
  const [colleges, setColleges] = useState([]);
  const [essays, setEssays] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [masterStrategy, setMasterStrategy] = useState({});
  const [assessmentData, setAssessmentData] = useState(null);
  const [personalProfile, _setPersonalProfile] = useState({});
  const [academicProfile, setAcademicProfile] = useState({});
  const [freeWrites, setFreeWrites] = useState([]);
  const [deadlines, setDeadlines] = useState(new Map()); // Map of collegeId -> deadlines array
  const [priorities, setPriorities] = useState(new Map()); // Map of collegeId -> priorities array
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recoveryError, setRecoveryError] = useState(null);
  
  const currentUserId = currentUser?.id;
  const familyId = family?.id;
  const syncChannelRef = useRef(null);

  // Recovery function for mock profile errors
  const handleRecovery = (realUserData) => {
    setRecoveryError(null);
    // Let FamilyContext handle the user switch
    // This will trigger a reload of all data with the real user
  };
  
  // Initialize real-time task synchronization
  useEffect(() => {
    if (currentUserId) {
      // Clean up any existing subscription
      if (syncChannelRef.current) {
        TaskSynchronizationService.cleanupSync(currentUserId);
      }
      
      // Initialize new sync channel
      syncChannelRef.current = TaskSynchronizationService.initializeSync(
        currentUserId,
        (update) => {
          console.log('[CollegeContext] Task sync update received:', update);
          
          // Handle different update types
          if (update.type === 'UPDATE' || update.type === 'INSERT') {
            // Check if the update affects timeline
            if (update.task && (update.task.epic_id || update.task.scheduled_date)) {
              console.log('[CollegeContext] Reloading timeline data due to task update');
              // Reload timeline data to reflect changes
              loadCollegeJourneyData(currentUserId);
            }
          }
        }
      );
      
      console.log('[CollegeContext] Task synchronization initialized for user:', currentUserId);
    }
    
    // Cleanup on unmount or user change
    return () => {
      if (syncChannelRef.current && currentUserId) {
        TaskSynchronizationService.cleanupSync(currentUserId);
        syncChannelRef.current = null;
      }
    };
  }, [currentUserId]);
  

  // Load college journey data from database
  const loadCollegeJourneyData = async (userId) => {
    if (!userId) {
      console.log('⚠️ loadCollegeJourneyData: No userId provided');
      return;
    }

    try {
      console.log('🎓 loadCollegeJourneyData: Starting load for user:', userId);
      setLoading(true);
      setError(null);
      
      // Reduced logging to prevent console spam
      if (!localStorage.getItem(`college-journey-${userId}`)) {
        console.log('🎓 Loading college journey data for user:', userId);
      }

      const cacheKey = `college-journey-${userId}`;

      // Try to load from cache first
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}-timestamp`);
      
      if (cachedData && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        if (cacheAge < 300000) { // 5 minutes
          const data = JSON.parse(cachedData);
          setColleges(data.colleges || []);
          setEssays(data.essays || []);
          setTimeline(data.timeline || []);
          setMasterStrategy(data.masterStrategy || {});
          setAssessmentData(data.assessmentData || null);
          setFreeWrites(data.freeWrites || []);
          if (data.personalProfile) {
            _setPersonalProfile(prev => ({ ...prev, [userId]: data.personalProfile }));
          }
          setAcademicProfile(data.academicProfile || {});
          // Reduced cache logging
          setLoading(false);
          return;
        }
      }

      // Load from database with individual error handling
      const [
        collegesData,
        essaysData,
        timelineData,
        masterStrategyData,
        freeWritesData,
        personalProfileData,
        academicProfileData,
        assessmentResponseData
      ] = await Promise.all([
        collegeJourneyService.getColleges(userId).catch(err => {
          console.error('❌ Error loading colleges:', err);
          // Check for UUID error with mock profile
          if (err.code === '22P02' && err.message?.includes('invalid input syntax for type uuid') && currentUser?.isMock) {
            setRecoveryError(err);
            return [];
          }
          throw err; // Re-throw to handle below
        }),
        collegeJourneyService.getEssays(userId).catch(err => {
          console.warn('⚠️ Essays table error:', err.message);
          return []; // Return empty array if error
        }),
        collegeJourneyService.getTimeline(userId).catch(err => {
          console.warn('⚠️ Timeline table error:', err.message);
          return []; // Return empty array if error
        }),
        collegeJourneyService.getMasterStrategy(userId).catch(err => {
          console.warn('⚠️ Master strategy table not available (406 expected):', err.message);
          return {}; // Return empty object if table doesn't exist
        }),
        collegeJourneyService.getFreeWrites(userId).catch(err => {
          console.warn('⚠️ Free writes table error:', err.message);
          return []; // Return empty array if error
        }),
        collegeJourneyService.getPersonalProfile(userId).catch(err => {
          console.warn('⚠️ Personal profile table error:', err.message);
          return {}; // Return empty object if error
        }),
        collegeJourneyService.getAcademicProfile(userId).catch(err => {
          console.warn('⚠️ Academic profile table error:', err.message);
          return {}; // Return empty object if error
        }),
        collegeJourneyService.getSavedAssessmentResponses(userId).catch(err => {
          console.warn('⚠️ Assessment responses table error:', err.message);
          console.warn('⚠️ Assessment responses error details:', err);
          return null; // Return null if error
        })
      ]);

      // Helper function to parse JSONB fields
      const parseJSONBField = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch (e) {
            // If JSON parsing fails, treat as a plain string and convert to array for consistency
            console.warn('JSONB field is plain string, converting to array:', value);
            return [value]; // Convert plain string to array
          }
        }
        return value;
      };

      // Transform college data to include strategy and parse JSONB fields
      const transformedColleges = collegesData.map(college => {
        const parsed = {
          ...college,
          // Parse JSONB fields
          student_advantages: parseJSONBField(college.student_advantages) || [],
          test_score_requirements: parseJSONBField(college.test_score_requirements) || {},
          application_priorities: parseJSONBField(college.application_priorities) || [],
          required_components: parseJSONBField(college.required_components) || [],
          optional_components: parseJSONBField(college.optional_components) || [],
          essay_prompts: parseJSONBField(college.essay_prompts) || [],
          essay_limits: parseJSONBField(college.essay_limits) || {},
          app_platforms: parseJSONBField(college.app_platforms) || [],
          strategy: college.college_journey_strategies?.[0] || {
            unique_angle: '',
            key_strengths: [],
            essay_focus: '',
            notes: ''
          }
        };
        
        // Debug logging for JSONB fields
        console.log(`🔍 College ${college.name} JSONB fields:`, {
          raw_student_advantages: college.student_advantages,
          parsed_student_advantages: parsed.student_advantages,
          raw_test_score_requirements: college.test_score_requirements,
          parsed_test_score_requirements: parsed.test_score_requirements,
          raw_application_priorities: college.application_priorities,
          parsed_application_priorities: parsed.application_priorities
        });
        
        return parsed;
      });

      // Update state
      setColleges(transformedColleges);
      setEssays(essaysData);
      setTimeline(timelineData);
      setMasterStrategy(masterStrategyData);
      
      // Debug assessment data loading
      console.log('🔍 Assessment Data Debug:', {
        userId,
        assessmentResponseData,
        hasAssessmentData: !!assessmentResponseData,
        assessmentDataType: typeof assessmentResponseData,
        assessmentDataKeys: assessmentResponseData ? Object.keys(assessmentResponseData) : 'N/A',
        expectedResultsUrl: assessmentResponseData ? `/college/strategic-profile/${assessmentResponseData.results_permalink || assessmentResponseData.id}` : 'N/A'
      });
      
      // If user has completed assessment, ensure onboarding is marked complete
      const hasCompletedAssessment = assessmentResponseData && (
        assessmentResponseData.completed_at || 
        assessmentResponseData.insights || 
        assessmentResponseData.formatted_insights ||
        (assessmentResponseData.responses && Object.keys(assessmentResponseData.responses).length > 0)
      );
      
      if (hasCompletedAssessment) {
        console.log('🎯 Assessment completed detected - checking onboarding status...');
        
        // Check current onboarding status
        try {
          const hasOnboardingAccess = await collegeJourneyProgressService.hasCollegeHubAccess(userId);
          
          if (!hasOnboardingAccess) {
            console.log('🔄 Onboarding not complete but assessment exists - marking onboarding complete...');
            await collegeJourneyProgressService.markOnboardingComplete(userId);
            console.log('✅ Onboarding marked complete due to assessment completion');
          } else {
            console.log('✅ Onboarding already complete');
          }
        } catch (error) {
          console.warn('⚠️ Could not update onboarding status:', error);
        }
      }

      // Check and set the final onboarding status
      try {
        const hasOnboardingAccess = await collegeJourneyProgressService.hasCollegeHubAccess(userId);
        setOnboardingComplete(hasOnboardingAccess);
        console.log('📊 Onboarding complete status:', hasOnboardingAccess);
      } catch (error) {
        console.warn('⚠️ Could not check onboarding status:', error);
        setOnboardingComplete(false);
      }
      
      setAssessmentData(assessmentResponseData);
      setFreeWrites(freeWritesData);
      _setPersonalProfile(prev => ({ ...prev, [userId]: personalProfileData }));
      setAcademicProfile(academicProfileData);

      // Cache the data
      const dataToCache = {
        colleges: transformedColleges,
        essays: essaysData,
        timeline: timelineData,
        masterStrategy: masterStrategyData,
        assessmentData: assessmentResponseData,
        freeWrites: freeWritesData,
        personalProfile: personalProfileData,
        academicProfile: academicProfileData
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
      
      console.log('✅ College journey data loaded from database');
    } catch (err) {
      console.error('❌ Error loading college journey data:', err);
      setError(err.message);
      
      // Try to fallback to cache
      try {
        const cacheKey = `college-journey-${userId}`;
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const data = JSON.parse(cachedData);
          setColleges(data.colleges || []);
          setEssays(data.essays || []);
          setTimeline(data.timeline || []);
          setMasterStrategy(data.masterStrategy || {});
          setAssessmentData(data.assessmentData || null);
          setFreeWrites(data.freeWrites || []);
          if (data.personalProfile) {
            _setPersonalProfile(prev => ({ ...prev, [userId]: data.personalProfile }));
          }
          setAcademicProfile(data.academicProfile || {});
          console.log('📦 Fallback to cached data due to error');
        }
      } catch (cacheError) {
        console.error('❌ Failed to load from cache:', cacheError);
      }
    } finally {
      console.log('🎓 loadCollegeJourneyData: Completed, setting loading to false');
      setLoading(false);
    }
  };

  // Load family-wide data for parent view
  const loadFamilyCollegeJourneyData = async () => {
    if (!familyId) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('👨‍👩‍👧‍👦 Loading family-wide college journey data for familyId:', familyId);
      console.log('👨‍👩‍👧‍👦 Current user context:', {
        currentUserId,
        'currentUser.id': currentUser?.id,
        'currentUser.auth_user_id': currentUser?.auth_user_id,
        'currentUser.type': currentUser?.type,
        'currentUser.isParent': currentUser?.isParent
      });

      const cacheKey = `family-college-journey-${familyId}`;

      // Try to load from cache first
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}-timestamp`);
      
      if (cachedData && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        if (cacheAge < 300000) { // 5 minutes
          const data = JSON.parse(cachedData);
          // Apply JSONB parsing to cached data too
          const parseJSONBField = (value) => {
            if (!value) return null;
            if (typeof value === 'string') {
              try {
                return JSON.parse(value);
              } catch (e) {
                console.warn('Failed to parse JSONB field from cache:', value);
                return null;
              }
            }
            return value;
          };

          const transformedCachedColleges = (data.colleges || []).map(college => ({
            ...college,
            student_advantages: parseJSONBField(college.student_advantages) || [],
            test_score_requirements: parseJSONBField(college.test_score_requirements) || {},
            application_priorities: parseJSONBField(college.application_priorities) || [],
            required_components: parseJSONBField(college.required_components) || [],
            optional_components: parseJSONBField(college.optional_components) || [],
            essay_prompts: parseJSONBField(college.essay_prompts) || [],
            essay_limits: parseJSONBField(college.essay_limits) || {},
            app_platforms: parseJSONBField(college.app_platforms) || []
          }));

          setColleges(transformedCachedColleges);
          setEssays(data.essays || []);
          setTimeline(data.timeline || []);
          console.log('📦 Loaded family college journey data from cache with JSONB parsing');
          setLoading(false);
          return;
        }
      }

      // Load from database
      const data = await collegeJourneyService.getFamilyCollegeJourneyData(familyId);
      
      console.log('📊 Family data loaded:', {
        colleges: data.colleges?.length || 0,
        essays: data.essays?.length || 0,
        timeline: data.timeline?.length || 0
      });
      
      // Helper function to parse JSONB fields
      const parseJSONBField = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch (e) {
            // If JSON parsing fails, treat as a plain string and convert to array for consistency
            console.warn('JSONB field is plain string, converting to array:', value);
            return [value]; // Convert plain string to array
          }
        }
        return value;
      };

      // Transform family college data to parse JSONB fields
      const transformedFamilyColleges = (data.colleges || []).map(college => {
        const parsed = {
          ...college,
          // Parse JSONB fields
          student_advantages: parseJSONBField(college.student_advantages) || [],
          test_score_requirements: parseJSONBField(college.test_score_requirements) || {},
          application_priorities: parseJSONBField(college.application_priorities) || [],
          required_components: parseJSONBField(college.required_components) || [],
          optional_components: parseJSONBField(college.optional_components) || [],
          essay_prompts: parseJSONBField(college.essay_prompts) || [],
          essay_limits: parseJSONBField(college.essay_limits) || {},
          app_platforms: parseJSONBField(college.app_platforms) || []
        };
        
        // Debug logging for family JSONB fields
        console.log(`🔍 Family College ${college.name} JSONB fields:`, {
          raw_student_advantages: college.student_advantages,
          parsed_student_advantages: parsed.student_advantages,
          raw_test_score_requirements: college.test_score_requirements,
          parsed_test_score_requirements: parsed.test_score_requirements,
          raw_application_priorities: college.application_priorities,
          parsed_application_priorities: parsed.application_priorities
        });
        
        return parsed;
      });
      
      setColleges(transformedFamilyColleges);
      setEssays(data.essays || []);
      setTimeline(data.timeline || []);
      
      // Cache the data
      const dataToCache = {
        colleges: transformedFamilyColleges,
        essays: data.essays || [],
        timeline: data.timeline || []
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
      
      console.log('✅ Family college journey data loaded from database');
    } catch (err) {
      console.error('❌ Error loading family college journey data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data when current user changes
  useEffect(() => {
    console.log('🔄 CollegeContext useEffect triggered:', {
      currentUserId,
      familyId,
      hasCurrentUser: !!currentUser,
      isParent: currentUser?.isParent,
      type: currentUser?.type,
      bothPresent: !!(currentUserId && familyId)
    });
    
    if (currentUserId && familyId) {
      // Check if current user is a parent
      const isParentCondition = currentUser?.isParent || currentUser?.type === 'parent';
      console.log('🔍 Parent condition check:', {
        'currentUser?.isParent': currentUser?.isParent,
        'currentUser?.type': currentUser?.type,
        'type === parent': currentUser?.type === 'parent',
        'final isParentCondition': isParentCondition
      });
      
      if (isParentCondition) {
        console.log('🔄 Loading family-wide data for parent view', {
          currentUserId,
          familyId,
          isParent: currentUser?.isParent,
          type: currentUser?.type
        });
        loadFamilyCollegeJourneyData();
      } else {
        console.log('🔄 Loading individual data for student view, calling loadCollegeJourneyData');
        loadCollegeJourneyData(currentUserId);
      }
    } else {
      // Clear data when no user selected
      console.log('🔄 Clearing data - no user or family selected', { currentUserId, familyId });
      setColleges([]);
      setEssays([]);
      setTimeline([]);
      setMasterStrategy({});
      setFreeWrites([]);
      _setPersonalProfile({});
      setAcademicProfile({});
    }
  }, [currentUserId, currentUser?.isParent, currentUser?.type, familyId]);

  // Function to load data for a specific child (for parent views)
  const loadChildData = async (childId) => {
    if (!childId) return;
    console.log('🔄 Loading child-specific data for:', childId);
    await loadCollegeJourneyData(childId);
  };

  // Invalidate cache when user switches
  const clearCache = (userId) => {
    if (userId) {
      const cacheKey = `college-journey-${userId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}-timestamp`);
    }
  };

  // College management functions
  const addCollege = async (collegeData) => {
    if (!currentUserId || !familyId) return null;
    
    try {
      console.log('🎓 Adding college:', collegeData);
      const newCollege = await addCollegeWithEvents(collegeData, currentUserId);
      console.log('✅ College added to database:', newCollege);
      
      // Transform to include strategy
      const transformedCollege = {
        ...newCollege,
        strategy: {
          unique_angle: '',
          key_strengths: [],
          essay_focus: '',
          notes: ''
        }
      };
      
      console.log('📝 Adding college to state:', transformedCollege);
      setColleges(prev => {
        const updated = [...prev, transformedCollege];
        console.log('📊 New colleges state:', updated);
        return updated;
      });
      clearCache(currentUserId);
      
      // Trigger completion rule evaluation after college is added
      try {
        const { default: CompletionRuleEvaluator } = await import('../services/CompletionRuleEvaluator');
        const evaluationResults = await CompletionRuleEvaluator.evaluateUserTasks(currentUserId);
        
        if (evaluationResults.completed > 0) {
          console.log(`🎯 Auto-completed ${evaluationResults.completed} tasks after adding college`);
          
          // Show toast notification for auto-completed tasks
          if (window.toast) {
            window.toast.success(`Auto-completed ${evaluationResults.completed} task${evaluationResults.completed === 1 ? '' : 's'}`);
          }
        }
      } catch (evaluationError) {
        console.warn('⚠️ Error evaluating completion rules after college addition:', evaluationError);
        // Don't throw - college was still added successfully
      }
      
      return transformedCollege;
    } catch (err) {
      console.error('❌ Error adding college:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateCollege = async (colId, updates, userId = null, skipUserCheck = false) => {
    // Use provided userId or fall back to currentUserId
    const targetUserId = userId || currentUserId;
    
    // For AI imports and family updates, we can skip the user check
    if (!skipUserCheck && !targetUserId) return;
    
    try {
      console.log('🔄 Updating college:', colId, updates, 'for user:', targetUserId, 'skipUserCheck:', skipUserCheck);
      
      // Check if current user is a parent viewing child data
      const isParentView = currentUser?.isParent || currentUser?.type === 'parent';
      
      // If parent is viewing family data, find the actual owner of this college
      let actualUserId = targetUserId;
      if (isParentView && !skipUserCheck) {
        const college = colleges.find(c => c.id === colId);
        if (college && college.user_id) {
          actualUserId = college.user_id;
          console.log('🔄 Parent updating child college - using actual owner ID:', actualUserId);
        } else {
          // If we can't find the college owner, skip user check to allow update
          skipUserCheck = true;
          console.log('🔄 Could not determine college owner - skipping user check');
        }
      }
      
      const updatedCollege = await collegeJourneyService.updateCollege(colId, updates, actualUserId, skipUserCheck);
      console.log('✅ College updated in database:', updatedCollege);
      
      setColleges(prev => {
        const updated = prev.map(college => 
          college.id === colId ? { ...college, ...updatedCollege } : college
        );
        console.log('📊 Updated colleges state:', updated);
        return updated;
      });
      clearCache(currentUserId);
      
      return updatedCollege;
    } catch (err) {
      console.error('❌ Error updating college:', err);
      setError(err.message);
      throw err;
    }
  };

  const removeCollege = async (colId, userId = null) => {
    if (!currentUserId) return;
    
    // Use provided userId or fall back to currentUserId
    const targetUserId = userId || currentUserId;
    
    try {
      await collegeJourneyService.removeCollege(colId, targetUserId);
      
      setColleges(prev => prev.filter(college => college.id !== colId));
      clearCache(currentUserId);
      
      // Trigger completion rule evaluation after college is removed
      // This may mark some tasks as incomplete if college count drops below thresholds
      try {
        const { default: CompletionRuleEvaluator } = await import('../services/CompletionRuleEvaluator');
        await CompletionRuleEvaluator.evaluateUserTasks(currentUserId);
        console.log('🔄 Re-evaluated completion rules after college removal');
      } catch (evaluationError) {
        console.warn('⚠️ Error evaluating completion rules after college removal:', evaluationError);
        // Don't throw - college was still removed successfully
      }
      
      return true;
    } catch (err) {
      console.error('❌ Error removing college:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateCollegeStrategy = async (colId, strategyUpdates) => {
    if (!currentUserId) return;
    
    try {
      await collegeJourneyService.updateCollegeStrategy(colId, strategyUpdates, currentUserId);
      
      setColleges(prev => prev.map(college => 
        college.id === colId 
          ? { 
              ...college, 
              ai_suggestions: { ...college.ai_suggestions, ...strategyUpdates },
              ai_suggestions_updated_at: new Date().toISOString()
            }
          : college
      ));
      clearCache(currentUserId);
      
      return true;
    } catch (err) {
      console.error('❌ Error updating college strategy:', err);
      setError(err.message);
      throw err;
    }
  };

  // Essay management functions
  const addEssay = async (essayData) => {
    if (!currentUserId || !familyId) return null;
    
    try {
      const newEssay = await collegeJourneyService.addEssay(essayData, currentUserId, familyId);
      setEssays(prev => [...prev, newEssay]);
      clearCache(currentUserId);
      return newEssay;
    } catch (err) {
      console.error('❌ Error adding essay:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateEssay = async (essayId, updates) => {
    if (!currentUserId) return;
    
    try {
      const updatedEssay = await collegeJourneyService.updateEssay(essayId, updates, currentUserId);
      setEssays(prev => prev.map(essay => 
        essay.id === essayId ? { ...essay, ...updatedEssay } : essay
      ));
      clearCache(currentUserId);
      return updatedEssay;
    } catch (err) {
      console.error('❌ Error updating essay:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateEssayContent = async (essayId, content) => {
    if (!currentUserId) return;
    
    const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    return await updateEssay(essayId, { content, word_count: wordCount });
  };

  const permanentDeleteEssay = async (essayId) => {
    if (!currentUserId) return;
    
    try {
      await collegeJourneyService.deleteEssay(essayId, true, currentUserId);
      setEssays(prev => prev.filter(essay => essay.id !== essayId));
      clearCache(currentUserId);
      return true;
    } catch (err) {
      console.error('❌ Error deleting essay:', err);
      setError(err.message);
      throw err;
    }
  };

  // Timeline management functions
  const addTimelineItem = async (timelineData) => {
    if (!currentUserId || !familyId) return null;
    
    try {
      const newItem = await collegeJourneyService.addTimelineItem(timelineData, currentUserId, familyId);
      setTimeline(prev => [...prev, newItem]);
      clearCache(currentUserId);
      return newItem;
    } catch (err) {
      console.error('❌ Error adding timeline item:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateTimelineItem = async (itemId, updates) => {
    if (!currentUserId) return;
    
    try {
      const updatedItem = await collegeJourneyService.updateTimelineItem(itemId, updates, currentUserId);
      setTimeline(prev => prev.map(item => 
        item.id === itemId ? { ...item, ...updatedItem } : item
      ));
      clearCache(currentUserId);
      return updatedItem;
    } catch (err) {
      console.error('❌ Error updating timeline item:', err);
      setError(err.message);
      throw err;
    }
  };

  const completeTimelineItem = async (itemId, shouldComplete = true) => {
    if (!currentUserId) return;
    
    if (shouldComplete) {
      return await updateTimelineItem(itemId, { 
        completed: true, 
        completion_date: new Date().toISOString() 
      });
    } else {
      // Mark as incomplete - remove completion date
      return await updateTimelineItem(itemId, { 
        completed: false, 
        completion_date: null 
      });
    }
  };

  // Comparison management functions
  const createComparison = (comparisonData) => {
    const newComparison = {
      id: `comparison_${Date.now()}`,
      ...comparisonData,
      created_date: new Date().toISOString()
    };
    setComparisons(prev => [...prev, newComparison]);
    return newComparison;
  };

  const updateComparison = (compId, updates) => {
    setComparisons(prev => prev.map(comp => 
      comp.id === compId ? { ...comp, ...updates } : comp
    ));
  };

  // Strategy management functions
  const updateMasterStrategy = async (strategyUpdates) => {
    if (!currentUserId) return;
    
    try {
      const updatedStrategy = await collegeJourneyService.updateMasterStrategy(strategyUpdates, currentUserId);
      setMasterStrategy(prev => ({ ...prev, ...updatedStrategy }));
      clearCache(currentUserId);
      return updatedStrategy;
    } catch (err) {
      console.error('❌ Error updating master strategy:', err);
      setError(err.message);
      throw err;
    }
  };

  // Mock data for strategic deep dive
  const getMockDeepDiveData = (userName) => {
    const mockData = {
      'Stella': {
        pattern_detective_responses: {
          "lose_track_time": "Reading books, especially fantasy and mystery novels. I can literally sit for 6 hours straight and not realize how much time has passed. I also get super absorbed when I'm writing stories or working on creative projects.",
          "friends_ask_help": "They usually come to me for help with writing essays, brainstorming creative ideas, or when they need someone to listen to relationship drama. I'm also the person they ask to help them figure out what classes to take or what extracurriculars to join.",
          "group_project_instinct": "Check if everyone is okay and feeling supported",
          "conversation_topic": "Books and stories, but also social justice issues. I always end up talking about representation in media or how certain policies affect teenagers, even if we started talking about something completely different.",
          "notice_others_miss": "I notice when people are feeling left out or uncomfortable in group settings. I can usually tell when someone has something they want to say but are hesitating, or when someone's acting different than usual.",
          "naturally_good_at": "Understanding what motivates people and helping them feel heard. I've always been able to connect with different types of people and help them see each other's perspectives.",
          "weird_skill": "I can remember the complete plot, main characters, and often specific quotes from pretty much every book I've ever read, even ones from elementary school. I also have this weird ability to predict how TV shows and movies will end after watching just one episode.",
          "rule_held_back": "My school has this policy where you can only be in one 'leadership position' at a time, which meant I couldn't be both editor of the literary magazine and president of the book club. It felt arbitrary because both roles actually complemented each other and would have made me better at both.",
          "would_break_again": "I organized an unauthorized student discussion group about books that weren't in our curriculum because our English classes only covered 'classic' literature by white male authors. The administration said it was 'disruptive' but students really needed space to discuss diverse voices.",
          "still_bothers_you": "The way schools handle mental health resources. They say they care about student wellbeing but then pile on homework and tests without considering that students are dealing with anxiety, family problems, or other real issues. It feels performative."
        },
        detected_patterns: [
          {
            theme: 'advocate',
            score: 95,
            title: 'The Advocate',
            description: 'Justice and fairness are core to who you are. Your responses show someone who stands up for what\'s right.',
            icon: '⚖️',
            color: 'indigo'
          },
          {
            theme: 'helper',
            score: 88,
            title: 'The Helper',
            description: 'Your responses show a consistent pattern of wanting to support and uplift others. You naturally gravitate toward making life better for people around you.',
            icon: '🤲',
            color: 'emerald'
          },
          {
            theme: 'connector',
            score: 82,
            title: 'The Connector',
            description: 'You see the world in terms of relationships and communities. Your strength lies in bringing people together and building bridges.',
            icon: '🌉',
            color: 'teal'
          }
        ],
        personalized_insights: {
          coreMotivation: "You are driven by a deep belief that everyone deserves to be heard and understood. Your responses reveal someone who sees systemic inequities and feels compelled to create spaces where marginalized voices can flourish.",
          uniqueStrength: "Your ability to identify when systems are failing people and then create alternative solutions that actually work. You don't just critique - you build better.",
          applicationStrategy: "Focus on specific examples where you created inclusive spaces or challenged systems that weren't serving everyone. Position yourself as someone who will make universities more equitable and accessible.",
          essayThemes: [
            "The unauthorized book discussion group and why diverse literature matters",
            "How you notice and support people who feel left out",
            "Times when you've challenged rules that didn't make sense",
            "Your vision for what inclusive education should look like"
          ],
          collegeMatchHints: "Look for schools with strong social justice programs, diverse student bodies, and a track record of student activism. You'd thrive at institutions that value student voice in creating change."
        }
      },
      'Beckett': {
        pattern_detective_responses: {
          "lose_track_time": "When I'm coding or working on any kind of technical project. I'll start building something at 2 PM and suddenly it's midnight and I haven't eaten dinner. Also when I'm researching random topics online - I can go down Wikipedia rabbit holes for hours.",
          "friends_ask_help": "Tech support, definitely. Fixing their computers, helping them set up accounts, explaining how apps work. But also weird problem-solving stuff - like figuring out the most efficient way to organize their room or schedule.",
          "group_project_instinct": "Step back to figure out what went wrong and how to fix it",
          "conversation_topic": "Technology and how it's changing everything, but also space exploration and scientific discoveries. I get really excited about breakthrough research and can't help but share it with people.",
          "notice_others_miss": "Patterns and inefficiencies in how things are organized or done. I see when there's obviously a better way to do something but everyone's just accepting the current system because 'that's how it's always been done.'",
          "naturally_good_at": "Breaking down complex problems into smaller, manageable pieces. I can look at something complicated and immediately see the logical steps needed to solve it or build it.",
          "weird_skill": "I've memorized the entire periodic table including atomic weights, and I can calculate compound interest and exponential growth in my head really quickly. I also know the specifications and performance stats of basically every smartphone released in the last 5 years.",
          "rule_held_back": "Our school's policy about not being allowed to test out of classes even if you already know the material. I taught myself calculus over the summer but still had to sit through pre-calc, which was incredibly frustrating and felt like a waste of time.",
          "would_break_again": "I set up a student-run tutoring network using Discord and shared Google drives, even though the school said we could only use 'official' platforms. Way more students got help this way than through the formal tutoring program.",
          "still_bothers_you": "How slowly schools adopt obviously beneficial technology. We're still using textbooks from 2015 and turning in physical papers when digital tools would be more efficient, more organized, and better for learning."
        },
        detected_patterns: [
          {
            theme: 'innovator',
            score: 92,
            title: 'The Innovator',
            description: 'You\'re not satisfied with the status quo. Your answers reveal someone who sees possibilities where others see limitations.',
            icon: '💡',
            color: 'violet'
          },
          {
            theme: 'analyzer',
            score: 87,
            title: 'The Analyzer',
            description: 'You have a systematic mind that loves to understand how things work. Your approach to problems is thorough and methodical.',
            icon: '🔍',
            color: 'amber'
          },
          {
            theme: 'builder',
            score: 84,
            title: 'The Builder',
            description: 'You have a drive to create and construct. Your answers reveal someone who wants to build solutions, not just talk about problems.',
            icon: '🔨',
            color: 'azure'
          }
        ],
        personalized_insights: {
          coreMotivation: "You are driven by efficiency and the potential of technology to solve real problems. Your responses show someone who gets frustrated by artificial limitations and is motivated to build better systems.",
          uniqueStrength: "Your ability to see inefficiencies and immediately envision better solutions, combined with the technical skills to actually build those improvements.",
          applicationStrategy: "Highlight specific examples where you've built solutions that outperformed existing systems. Show how you'll bring innovation and optimization to university programs.",
          essayThemes: [
            "The Discord tutoring network and why student-created solutions worked better",
            "Teaching yourself advanced topics and why traditional pacing doesn't fit all learners",
            "How you see technology solving problems others haven't even identified yet",
            "Times when you've improved systems through creative technical solutions"
          ],
          collegeMatchHints: "Seek out schools with strong CS/engineering programs, opportunities for independent research, and a culture that values student innovation. Look for places where you can build, not just study."
        }
      },
      'Sarah': {
        pattern_detective_responses: {
          "lose_track_time": "When I'm working on art projects, especially painting or digital design. I'll start working on something after school and suddenly my parents are calling me for dinner and I realize I've been in my room for 4 hours straight.",
          "friends_ask_help": "Creative stuff mostly - designing posters for events, helping them figure out how to decorate their rooms, or brainstorming ideas for projects. But also advice about dealing with family drama or figuring out what they actually want to do after high school.",
          "group_project_instinct": "Take charge, reorganize, and assign tasks to different members",
          "conversation_topic": "Art and design, but I also always end up talking about environmental issues and sustainability. I care a lot about how our choices affect the planet and I guess I bring it up more than I realize.",
          "notice_others_miss": "Visual details and how spaces make people feel. I notice when a room or environment is affecting people's mood, or when someone's personal style is reflecting something they're going through.",
          "naturally_good_at": "Seeing the big picture and helping people organize their thoughts or plans. I'm good at taking someone's scattered ideas and helping them see how everything fits together.",
          "weird_skill": "I can look at any space - a room, a store, whatever - and immediately redesign it in my head to be more functional and aesthetically pleasing. I also remember specific color combinations I've seen and can recreate them perfectly later.",
          "rule_held_back": "Our art program has these really strict guidelines about what mediums we can use for projects, which limited my ability to do mixed-media work that I think would be more impactful and creative.",
          "would_break_again": "I started a student-led initiative to replace disposable lunch trays with reusable ones, even though I wasn't supposed to 'make policy suggestions as a student.' The environmental impact was too important to ignore.",
          "still_bothers_you": "How schools claim to care about creativity and individual expression but then have such rigid structures that don't actually allow for creative thinking or alternative approaches to learning."
        },
        detected_patterns: [
          {
            theme: 'creative',
            score: 91,
            title: 'The Creative',
            description: 'You approach the world with an artistic lens. Your answers reveal someone who values expression and originality.',
            icon: '🎨',
            color: 'pink'
          },
          {
            theme: 'leader',
            score: 86,
            title: 'The Leader',
            description: 'Your responses show natural organizational and leadership tendencies. You don\'t just see problems - you mobilize solutions.',
            icon: '🚀',
            color: 'red'
          },
          {
            theme: 'advocate',
            score: 83,
            title: 'The Advocate',
            description: 'Justice and fairness are core to who you are. Your responses show someone who stands up for what\'s right.',
            icon: '⚖️',
            color: 'indigo'
          }
        ],
        personalized_insights: {
          coreMotivation: "You are driven by a vision of how things could be better - more beautiful, more sustainable, more authentic. Your responses show someone who sees potential everywhere and feels compelled to bring that vision to life.",
          uniqueStrength: "Your ability to see both the aesthetic and functional potential in any situation, combined with the leadership skills to actually make improvements happen.",
          applicationStrategy: "Focus on examples where you've used creativity to solve real problems or create positive change. Show how your artistic vision translates into practical leadership.",
          essayThemes: [
            "The lunch tray initiative and how design thinking applies to environmental problems",
            "How you help people organize their thoughts through visual/spatial approaches",
            "Times when you've pushed against creative limitations to achieve better outcomes",
            "Your vision for sustainable design and environmental responsibility"
          ],
          collegeMatchHints: "Look for schools that value interdisciplinary approaches, especially where art meets environmental science or policy. You'd thrive where creative expression is seen as a tool for social change."
        }
      }
    };
    
    return mockData[userName] || null;
  };

  // Function to populate mock data for testing
  const populateMockData = async (userName) => {
    const mockData = getMockDeepDiveData(userName);
    if (mockData) {
      await updateMasterStrategy(mockData);
      return mockData;
    }
    return null;
  };

  // Personal profile management functions
  const loadPersonalProfile = async (userId = currentUserId) => {
    if (!userId || !familyId) return;
    
    try {
      const profile = await collegeJourneyService.getPersonalProfile(userId, familyId);
      _setPersonalProfile(prev => ({
        ...prev,
        [userId]: profile
      }));
      return profile;
    } catch (err) {
      console.error('❌ Error loading personal profile:', err);
      setError(err.message);
      throw err;
    }
  };

  const updatePersonalProfile = async (profileUpdates) => {
    if (!currentUserId || !familyId) return;
    
    try {
      const updatedProfile = await collegeJourneyService.updatePersonalProfile(profileUpdates, currentUserId, familyId);
      _setPersonalProfile(prev => ({
        ...prev,
        [currentUserId]: updatedProfile.profile_data
      }));
      clearCache(currentUserId);
      return updatedProfile;
    } catch (err) {
      console.error('❌ Error updating personal profile:', err);
      setError(err.message);
      throw err;
    }
  };

  // Academic profile management functions
  const loadAcademicProfile = async (userId = currentUserId) => {
    if (!userId || !familyId) return;
    
    try {
      const profile = await collegeJourneyService.getAcademicProfile(userId, familyId);
      setAcademicProfile(profile);
      return profile;
    } catch (err) {
      console.error('❌ Error loading academic profile:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateAcademicProfile = async (profileUpdates) => {
    if (!currentUserId || !familyId) return;
    
    try {
      const updatedProfile = await collegeJourneyService.updateAcademicProfile(profileUpdates, currentUserId, familyId);
      setAcademicProfile(updatedProfile);
      clearCache(currentUserId);
      return updatedProfile;
    } catch (err) {
      console.error('❌ Error updating academic profile:', err);
      setError(err.message);
      throw err;
    }
  };

  // Student activities and honors functions
  const getStudentActivities = async (userId = currentUserId) => {
    if (!userId || !familyId) return [];
    
    try {
      const activities = await collegeJourneyService.getStudentActivities(userId, familyId);
      return activities;
    } catch (err) {
      console.error('❌ Error loading student activities:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateStudentActivities = async (activities) => {
    if (!currentUserId || !familyId) return;
    
    try {
      const updatedActivities = await collegeJourneyService.updateStudentActivities(activities, currentUserId, familyId);
      clearCache(currentUserId);
      return updatedActivities;
    } catch (err) {
      console.error('❌ Error updating student activities:', err);
      setError(err.message);
      throw err;
    }
  };

  const getStudentHonors = async (userId = currentUserId) => {
    if (!userId || !familyId) return [];
    
    try {
      const honors = await collegeJourneyService.getStudentHonors(userId, familyId);
      return honors;
    } catch (err) {
      console.error('❌ Error loading student honors:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateStudentHonors = async (honors) => {
    if (!currentUserId || !familyId) return;
    
    try {
      const updatedHonors = await collegeJourneyService.updateStudentHonors(honors, currentUserId, familyId);
      clearCache(currentUserId);
      return updatedHonors;
    } catch (err) {
      console.error('❌ Error updating student honors:', err);
      setError(err.message);
      throw err;
    }
  };

  // Free writing management functions
  const addFreeWrite = async (content = '', date = null, additionalData = {}) => {
    if (!currentUserId || !familyId) return null;
    
    try {
      const now = new Date().toISOString();
      const freeWriteData = {
        date: date || new Date().toISOString().split('T')[0],
        content,
        word_count: content.trim().split(/\s+/).filter(word => word.length > 0).length,
        created_at: now,
        last_modified: now,
        started_at: now,
        ...additionalData
      };
      
      let freeWrite = await collegeJourneyService.getOrCreateFreeWrite(freeWriteData.date, currentUserId, familyId);
      
      // If there's content to save, update it and get the updated record
      if (freeWriteData.content) {
        const updatedFreeWrite = await collegeJourneyService.updateFreeWrite(freeWrite.id, freeWriteData.content, currentUserId);
        freeWrite = updatedFreeWrite; // Use the updated version with the new content
      }
      
      // Update or add to local state with the correct data
      setFreeWrites(prev => {
        const existing = prev.find(fw => fw.date === freeWrite.date);
        if (existing) {
          return prev.map(fw => fw.date === freeWrite.date ? freeWrite : fw);
        } else {
          return [...prev, freeWrite];
        }
      });
      
      clearCache(currentUserId);
      return freeWrite;
    } catch (err) {
      console.error('❌ Error adding free write:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateFreeWrite = async (id, content) => {
    if (!currentUserId) return;
    
    const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    const freeWrite = freeWrites.find(fw => fw.id === id);
    
    if (freeWrite) {
      const now = new Date().toISOString();
      const additionalData = {
        created_at: freeWrite.created_at || now,
        started_at: freeWrite.started_at || now,
        last_modified: now
      };
      
      return await addFreeWrite(content, freeWrite.date, additionalData);
    }
  };

  const getFreeWriteByDate = (date) => {
    return freeWrites.find(fw => fw.date === date);
  };

  const getOrCreateFreeWrite = async (date) => {
    const existing = getFreeWriteByDate(date);
    if (existing) return existing;
    
    // Create new free write with proper timestamps
    const now = new Date().toISOString();
    const freeWriteData = {
      date: date || new Date().toISOString().split('T')[0],
      content: '',
      word_count: 0,
      created_at: now,
      last_modified: now,
      started_at: now
    };
    
    return await addFreeWrite('', date, freeWriteData);
  };

  // Deadline management functions
  const getCollegeDeadlines = (collegeId) => {
    return deadlines.get(collegeId) || [];
  };

  const loadCollegeDeadlines = useCallback(async (collegeId, forceReload = false) => {
    try {
      // Clear cached deadlines if force reload requested
      if (forceReload) {
        console.log('🔄 Force reloading deadlines, clearing cache for college:', collegeId);
        setDeadlines(prev => {
          const newMap = new Map(prev);
          newMap.delete(collegeId);
          return newMap;
        });
      }
      
      const deadlinesData = await collegeJourneyService.getCollegeDeadlines(collegeId);
      setDeadlines(prev => new Map(prev.set(collegeId, deadlinesData || [])));
      return deadlinesData || [];
    } catch (err) {
      console.error('❌ Error loading college deadlines:', err);
      
      // Handle specific error types without rethrowing to prevent loops
      if (err.code === '22P02' && err.message?.includes('invalid input syntax for type uuid')) {
        // Check if we're in a mock profile
        const currentUserStr = localStorage.getItem('currentUser');
        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            if (currentUser?.isMock) {
              console.warn('🔄 Mock profile causing UUID errors, attempting recovery...');
              
              // Try to restore real user
              const realUser = localStorage.getItem('real-user-context');
              if (realUser) {
                const realUserData = JSON.parse(realUser);
                // Trigger a page reload to reset all contexts
                localStorage.removeItem('real-user-context');
                window.location.href = '/college?recovered=true';
                return [];
              }
            }
          } catch (parseError) {
            console.error('Error parsing user context during recovery:', parseError);
          }
        }
      }
      
      // Handle service unavailable or network errors gracefully
      if (err.message?.includes('503') || err.message?.includes('Service Unavailable') || err.message?.includes('Failed to fetch')) {
        console.warn('⚠️ Service temporarily unavailable, using empty deadlines array to prevent loops');
        setError('Service temporarily unavailable. Please try again later.');
        
        // Set empty array to prevent undefined issues and stop retries
        setDeadlines(prev => new Map(prev.set(collegeId, [])));
        return [];
      }
      
      // For other errors, set error state but don't rethrow to prevent loops
      setError(err.message);
      setDeadlines(prev => new Map(prev.set(collegeId, [])));
      return [];
    }
  }, []); // Empty dependency array since this function doesn't depend on any props or state

  const addCollegeDeadline = async (deadlineData, collegeId) => {
    try {
      const newDeadline = await collegeJourneyService.addCollegeDeadline(deadlineData, collegeId);
      setDeadlines(prev => {
        const updated = new Map(prev);
        const currentDeadlines = updated.get(collegeId) || [];
        updated.set(collegeId, [...currentDeadlines, newDeadline]);
        return updated;
      });
      clearCache(currentUserId);
      return newDeadline;
    } catch (err) {
      console.error('❌ Error adding college deadline:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateCollegeDeadline = async (deadlineId, updates, collegeId) => {
    try {
      const updatedDeadline = await collegeJourneyService.updateCollegeDeadline(deadlineId, updates);
      setDeadlines(prev => {
        const updated = new Map(prev);
        const currentDeadlines = updated.get(collegeId) || [];
        updated.set(collegeId, currentDeadlines.map(d => d.id === deadlineId ? {...d, ...updatedDeadline} : d));
        return updated;
      });
      clearCache(currentUserId);
      return updatedDeadline;
    } catch (err) {
      console.error('❌ Error updating college deadline:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteCollegeDeadline = async (deadlineId, collegeId) => {
    try {
      await collegeJourneyService.deleteCollegeDeadline(deadlineId);
      setDeadlines(prev => {
        const updated = new Map(prev);
        const currentDeadlines = updated.get(collegeId) || [];
        updated.set(collegeId, currentDeadlines.filter(d => d.id !== deadlineId));
        return updated;
      });
      clearCache(currentUserId);
      return true;
    } catch (err) {
      console.error('❌ Error deleting college deadline:', err);
      setError(err.message);
      throw err;
    }
  };

  // Priority management functions
  const getCollegePriorities = (collegeId) => {
    return priorities.get(collegeId) || [];
  };

  const loadCollegePriorities = useCallback(async (collegeId) => {
    try {
      const prioritiesData = await collegeJourneyService.getCollegePriorities(collegeId);
      setPriorities(prev => new Map(prev.set(collegeId, prioritiesData)));
      return prioritiesData;
    } catch (err) {
      console.error('❌ Error loading college priorities:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Generate immediate priorities dynamically from deadlines and timeline
  const generateImmediatePriorities = (collegeId) => {
    const collegeTimeline = timeline.filter(item => item.college_id === collegeId && !item.completed);
    const collegeDeadlines = getCollegeDeadlines(collegeId);
    
    const now = new Date();
    const urgent = [];
    
    // Add urgent timeline items (due within 2 weeks)
    const urgentTimeline = collegeTimeline.filter(item => {
      if (!item.due_date) return false;
      const dueDate = new Date(item.due_date);
      const daysDiff = (dueDate - now) / (1000 * 60 * 60 * 24);
      return daysDiff <= 14 && daysDiff >= 0;
    });
    
    urgent.push(...urgentTimeline.map(item => ({
      type: 'timeline',
      title: item.title,
      description: item.description,
      due_date: item.due_date,
      urgency: 'high'
    })));
    
    // Add urgent deadlines (due within 3 weeks)
    const urgentDeadlines = collegeDeadlines.filter(deadline => {
      const dueDate = parseLocalDate(deadline.due_date);
      const daysDiff = (dueDate - now) / (1000 * 60 * 60 * 24);
      return daysDiff <= 21 && daysDiff >= 0;
    });

    urgent.push(...urgentDeadlines.map(deadline => ({
      type: 'deadline',
      title: deadline.title,
      description: deadline.description,
      due_date: deadline.due_date,
      urgency: deadline.urgency_level || 'normal'
    })));

    // Sort by due date
    return urgent.sort((a, b) => parseLocalDate(a.due_date) - parseLocalDate(b.due_date));
  };

  // Utility functions
  const getCollegesByCategory = (category) => {
    return colleges.filter(college => college.category === category);
  };

  const getEssaysByType = (type) => {
    return essays.filter(essay => essay.type === type);
  };

  const getEssaysByCollege = (collegeId) => {
    return essays.filter(essay => essay.college_id === collegeId);
  };

  const getUpcomingDeadlines = (days = 30) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + days);
    
    return timeline
      .filter(item => !item.completed && item.due_date)
      .filter(item => new Date(item.due_date) <= cutoffDate)
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  const getApplicationProgress = () => {
    const totalColleges = colleges.length;
    const inProgress = colleges.filter(c => c.application_status === 'in_progress').length;
    const submitted = colleges.filter(c => c.application_status === 'submitted').length;
    const completed = colleges.filter(c => c.application_status === 'completed').length;
    
    return {
      total: totalColleges,
      planning: colleges.filter(c => c.application_status === 'planning').length,
      in_progress: inProgress,
      submitted: submitted, 
      completed: completed,
      percentage: totalColleges > 0 ? Math.round(((submitted + completed) / totalColleges) * 100) : 0
    };
  };

  // Create currentStudent object for components that expect it
  const currentStudent = {
    id: currentUserId,
    profile: academicProfile,
    assessmentData: assessmentData,
    personalProfile: personalProfile,
    isParent: currentUser?.isParent,
    type: currentUser?.type
  };

  // College category lock/unlock state
  const [lockedCategories, setLockedCategories] = useState({});
  const [customTargets, setCustomTargets] = useState({});

  // Load completion locks from profile
  const loadCompletionLocks = useCallback(async () => {
    if (!currentUserId) return;

    try {
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('locked_college_categories, custom_college_targets')
        .eq('user_id', currentUserId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading completion locks:', error);
        return;
      }

      if (data) {
        setLockedCategories(data.locked_college_categories || {});
        setCustomTargets(data.custom_college_targets || {});
      }
    } catch (error) {
      console.error('Error loading completion locks:', error);
    }
  }, [currentUserId]);

  // Save completion locks to profile
  const saveCompletionLocks = useCallback(async (locks, targets) => {
    if (!currentUserId) return;

    try {
      // First, try to check if the columns exist by attempting a select
      const { data: existingProfile, error: selectError } = await supabase
        .from('student_academic_profiles')
        .select('user_id, locked_college_categories, custom_college_targets')
        .eq('user_id', currentUserId)
        .single();

      if (selectError && selectError.code === '42703') {
        // Column doesn't exist - migration hasn't been applied
        console.warn('⚠️ Completion lock columns not found. Please apply the database migration first.');
        console.warn('Migration file: src/migrations/20250905_add_completion_locks.sql');
        return;
      }

      // Try to update existing profile first
      const { data: updateResult, error: updateError } = await supabase
        .from('student_academic_profiles')
        .update({
          locked_college_categories: locks,
          custom_college_targets: targets,
          completion_locks_updated_at: new Date().toISOString()
        })
        .eq('user_id', currentUserId)
        .select();

      // If update didn't affect any rows, insert a new one
      if (updateResult && updateResult.length === 0) {
        const { error: insertError } = await supabase
          .from('student_academic_profiles')
          .insert({
            user_id: currentUserId,
            locked_college_categories: locks,
            custom_college_targets: targets,
            completion_locks_updated_at: new Date().toISOString()
          });
        
        if (insertError) throw insertError;
      } else if (updateError) {
        throw updateError;
      }
      
      console.log('✅ Completion locks saved successfully');
    } catch (error) {
      console.error('Error saving completion locks:', error);
      
      // If it's a constraint error, provide helpful message
      if (error.code === '42P10') {
        console.warn('⚠️ Database migration required. Please run: src/migrations/20250905_add_completion_locks.sql');
      }
      
      throw error;
    }
  }, [currentUserId]);

  // Toggle lock/unlock for a college category
  const toggleCategoryLock = useCallback(async (categoryKey, isLocked) => {
    try {
      const newLockedCategories = { ...lockedCategories, [categoryKey]: isLocked };
      setLockedCategories(newLockedCategories);
      await saveCompletionLocks(newLockedCategories, customTargets);
      
      console.log(`🔒 ${isLocked ? 'Locked' : 'Unlocked'} ${categoryKey} category`);
      
      // Re-evaluate completion rules after lock change
      try {
        const { CompletionRuleEvaluator } = await import('../services/CompletionRuleEvaluator');
        await CompletionRuleEvaluator.evaluateUserTasks(currentUserId);
        console.log('🔄 Re-evaluated completion rules after lock change');
      } catch (evaluationError) {
        console.warn('⚠️ Error evaluating completion rules after lock change:', evaluationError);
      }
    } catch (error) {
      console.error('Error toggling category lock:', error);
      // Revert state change on error
      setLockedCategories(lockedCategories);
    }
  }, [lockedCategories, customTargets, currentUserId, saveCompletionLocks]);

  // Set custom target for a college category
  const setCustomTarget = useCallback(async (categoryKey, target) => {
    try {
      const newCustomTargets = { ...customTargets, [categoryKey]: target };
      setCustomTargets(newCustomTargets);
      await saveCompletionLocks(lockedCategories, newCustomTargets);
      
      console.log(`🎯 Set custom target for ${categoryKey}: ${target}`);
    } catch (error) {
      console.error('Error setting custom target:', error);
      // Revert state change on error
      setCustomTargets(customTargets);
    }
  }, [customTargets, lockedCategories, saveCompletionLocks]);

  // Load completion locks when user changes
  useEffect(() => {
    if (currentUserId) {
      loadCompletionLocks();
    }
  }, [currentUserId, loadCompletionLocks]);

  const contextValue = {
    // State
    colleges,
    essays,
    timeline,
    comparisons,
    masterStrategy,
    assessmentData,
    personalProfile,
    academicProfile,
    freeWrites,
    deadlines, // Map of collegeId -> deadlines array
    priorities, // Map of collegeId -> priorities array
    onboardingComplete,
    loading,
    error,

    // Completion locks
    lockedCategories,
    customTargets,
    
    // Current user/student info
    currentStudent,
    currentUserId,

    // Data management
    loadCollegeJourneyData,
    loadChildData,
    clearCache,

    // College functions
    addCollege,
    updateCollege,
    removeCollege,
    updateCollegeStrategy,
    getCollegesByCategory,
    
    // Debug functions
    createSampleColleges: (userId, addedByUserId) => collegeJourneyService.createSampleColleges(userId, addedByUserId),

    // Essay functions
    addEssay,
    updateEssay,
    updateEssayContent,
    permanentDeleteEssay,
    getEssaysByType,
    getEssaysByCollege,

    // Timeline functions
    addTimelineItem,
    updateTimelineItem,
    completeTimelineItem,
    getUpcomingDeadlines,

    // Comparison functions
    createComparison,
    updateComparison,

    // Strategy functions
    updateMasterStrategy,
    populateMockData,

    // Completion lock functions
    toggleCategoryLock,
    setCustomTarget,
    loadCompletionLocks,

    // Personal profile functions
    loadPersonalProfile,
    updatePersonalProfile,

    // Academic profile functions
    loadAcademicProfile,
    updateAcademicProfile,
    getStudentActivities,
    updateStudentActivities,
    getStudentHonors,
    updateStudentHonors,

    // Free writing functions
    addFreeWrite,
    updateFreeWrite,
    getFreeWriteByDate,
    getOrCreateFreeWrite,

    // Deadline functions
    getCollegeDeadlines,
    loadCollegeDeadlines,
    addCollegeDeadline,
    updateCollegeDeadline,
    deleteCollegeDeadline,

    // Priority functions
    getCollegePriorities,
    loadCollegePriorities,
    generateImmediatePriorities,

    // Utility functions
    getApplicationProgress,

    // Student Verification functions (manual overrides of AI data)
    updateStudentDeadline: async (collegeId, deadlineType, newDate) => {
      const result = await StudentVerificationService.updateDeadline(collegeId, deadlineType, newDate, currentUserId);
      if (result.success) {
        // Refresh colleges and timeline to show updated data
        await loadCollegeJourneyData(currentUserId);
        clearCache(currentUserId);
      }
      return result;
    },
    updateStudentEssayPrompts: async (collegeId, newPrompts) => {
      const result = await StudentVerificationService.updateEssayPrompts(collegeId, newPrompts, currentUserId);
      if (result.success) {
        // Refresh colleges, essays, and timeline
        await loadCollegeJourneyData(currentUserId);
        clearCache(currentUserId);
      }
      return result;
    },
    updateStudentRequirements: async (collegeId, requirements) => {
      const result = await StudentVerificationService.updateApplicationRequirements(collegeId, requirements, currentUserId);
      if (result.success) {
        // Refresh colleges
        await loadCollegeJourneyData(currentUserId);
        clearCache(currentUserId);
      }
      return result;
    },
    getVerificationStatus: (collegeId) => StudentVerificationService.getVerificationStatus(collegeId),

    // Essay Prompt Choice Group Methods
    finalizePromptChoice: async (slotId) => {
      const result = await EssaySlotService.finalizePromptChoice(slotId);
      if (result.success) {
        // Refresh to show updated slot statuses
        await loadCollegeJourneyData(currentUserId);
      }
      return result;
    },
    updateSlotSelectionStatus: async (slotId, newStatus) => {
      const result = await EssaySlotService.updateSlotSelectionStatus(slotId, newStatus);
      if (result.success) {
        // Refresh to show updated slot statuses
        await loadCollegeJourneyData(currentUserId);
      }
      return result;
    },
    validatePromptGroupCompletion: async (promptGroupId) => {
      return await EssaySlotService.validatePromptGroupCompletion(promptGroupId);
    },
    getSlotsByGroupId: async (promptGroupId) => {
      return await EssaySlotService.getSlotsByGroupId(promptGroupId);
    },

    // Constants
    COLLEGE_CATEGORIES,
    ESSAY_TYPES,
    APPLICATION_ROUNDS,
    TIMELINE_CATEGORIES
  };

  return (
    <CollegeContext.Provider value={contextValue}>
      {children}
      <TestingErrorRecovery error={recoveryError} onRecover={handleRecovery} />
    </CollegeContext.Provider>
  );
}